import { Theme } from "@emotion/react";
import { useTheme } from "@mui/material";
import { IconName, IconPrefix, IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnalysisToolDropdownIcon, ActionDropdownButton, KeyboardShortcutToolTipComponent, InfoTooltip } from "@iventis/components";
import { StyleType } from "@iventis/domain-model/model/styleType";
import React, { useContext, useEffect, useState } from "react";
import { machineEventTypes, ModeEvents } from "@iventis/map-engine/src/machines/map-machines.types";
import { MapEvents } from "@iventis/map-engine/src/types/map-stream.types";
import { EventStream, useCtrlKey } from "@iventis/utilities";
import { analysisLineLayerId, analysisPolygonLayerId } from "@iventis/map-engine/src/bridge/constants";
import { UndoRedoContext } from "@iventis/utilities/src/undo-redo/undo-redo-v2-context";
import { AnalysisType } from "@iventis/domain-model/model/analysisType";
import { MapObjectProperties } from "@iventis/map-types";
import { Content } from "@iventis/translations";
import { UseIventisTranslate, useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FixedShape } from "@iventis/domain-model/model/fixedShape";

import {
    borderRadius,
    smallToolbarButton,
    toolbarButton,
    toolbarButtonPadding,
    toolbarCommentButtonHeight,
    toolbarCommentButtonPadding,
    toolbarCommentButtonWidth,
} from "@iventis/styles/src/atomic-rules";
import { styled } from "@iventis/styles";
import { styleTypeToGeoJSONGeometryType } from "@iventis/map-engine/src/utilities/geojson-helpers";
import { DrawingModifier } from "@iventis/map-engine/src/types/store-schema";
import { ModeOfTransport } from "@iventis/domain-model/model/modeOfTransport";
import { TypedFeature } from "@iventis/map-engine/src/types/internal";
import {
    ConfirmAction,
    ToolBarButton,
    CommentsButton,
    CommentsVisibilityButton,
    UndoButton,
    RedoButton,
    CopyMapObjects,
} from "@iventis/map-control-components/src/draw-toolbar-buttons";

export enum ObjectSelectType {
    ObjectSelect = "ObjectSelect",
    AreaSelect = "AreaSelect",
}

export enum CommentModeType {
    Commenting = "Commenting",
}

export enum EditingAction {
    LineSplitting = "LineSplitting",
}

const analysisIcons: Record<
    AnalysisType,
    {
        iconName: string;
        prefix: string;
    }
> = {
    [AnalysisType.Distance]: { iconName: "ruler", prefix: "far" },
    [AnalysisType.WalkTime]: { iconName: "person-walking", prefix: "far" },
    [AnalysisType.FlowRate]: { iconName: "door-open", prefix: "far" },
    [AnalysisType.Area]: { iconName: "ruler-combined", prefix: "far" },
    [AnalysisType.Capacity]: { iconName: "users", prefix: "far" },
    [AnalysisType.Count]: { iconName: "tally", prefix: "far" },
};

const analysisMenuItems = (setIcon: (analysisType: AnalysisType) => void, onAnalysisToolClicked: (analysisTool: AnalysisType) => void, translate: UseIventisTranslate) => [
    {
        displayName: translate(Content.map2.analysis.distance_title),
        testId: "analysis-tool-distance",
        onClick: () => {
            setIcon(AnalysisType.Distance);
            onAnalysisToolClicked(AnalysisType.Distance);
        },
        primaryIcon: ["far", "ruler"],
        secondaryIcon: (
            <span style={{ color: "#B6B6B6", height: "12px", width: "12px" }}>
                <AnalysisToolDropdownIcon icon="Line" />
            </span>
        ),
    },
    {
        displayName: translate(Content.map2.analysis.walk_time_title),
        testId: "analysis-tool-walk-time",
        onClick: () => {
            setIcon(AnalysisType.WalkTime);
            onAnalysisToolClicked(AnalysisType.WalkTime);
        },
        primaryIcon: ["far", "person-walking"],
        secondaryIcon: (
            <span style={{ color: "#B6B6B6", height: "12px", width: "12px" }}>
                <AnalysisToolDropdownIcon icon="Line" />
            </span>
        ),
    },
    {
        displayName: translate(Content.map2.analysis.flow_rate_title),
        testId: "analysis-tool-flow-rate",
        onClick: () => {
            setIcon(AnalysisType.FlowRate);
            onAnalysisToolClicked(AnalysisType.FlowRate);
        },
        divider: true,
        primaryIcon: ["far", "door-open"],
        secondaryIcon: (
            <span style={{ color: "#B6B6B6", height: "12px", width: "12px" }}>
                <AnalysisToolDropdownIcon icon="Line" />
            </span>
        ),
    },
    {
        displayName: translate(Content.map2.analysis.area_title),
        testId: "analysis-tool-area",
        onClick: () => {
            setIcon(AnalysisType.Area);
            onAnalysisToolClicked(AnalysisType.Area);
        },
        primaryIcon: ["far", "ruler-combined"],
        secondaryIcon: (
            <span style={{ color: "#B6B6B6", height: "12px", width: "12px" }}>
                <AnalysisToolDropdownIcon icon="Polygon" />
            </span>
        ),
    },
    {
        displayName: translate(Content.map2.analysis.capacity_title),
        testId: "analysis-tool-capacity",
        onClick: () => {
            setIcon(AnalysisType.Capacity);
            onAnalysisToolClicked(AnalysisType.Capacity);
        },
        primaryIcon: ["far", "users"],
        secondaryIcon: (
            <span style={{ color: "#B6B6B6", height: "12px", width: "12px" }}>
                <AnalysisToolDropdownIcon icon="Polygon" />
            </span>
        ),
    },
    {
        displayName: translate(Content.map2.analysis.count_title),
        testId: "analysis-tool-count",
        onClick: () => {
            setIcon(AnalysisType.Count);
            onAnalysisToolClicked(AnalysisType.Count);
        },
        primaryIcon: ["far", "tally"],
        secondaryIcon: (
            <span style={{ color: "#B6B6B6", height: "12px", width: "12px" }}>
                <AnalysisToolDropdownIcon icon="Polygon" />
            </span>
        ),
    },
];

const selectionToolIcons: Record<ObjectSelectType, { iconName: string; prefix: string }> = {
    [ObjectSelectType.ObjectSelect]: { iconName: "arrow-pointer", prefix: "far" },
    [ObjectSelectType.AreaSelect]: { iconName: "area-select", prefix: "far" },
};

const selectionToolMenuItems = (setIcon: (objectSelectType: ObjectSelectType) => void, noLayerSelected: boolean, translate: UseIventisTranslate) => [
    {
        displayName: translate(Content.map9.select.select_object),
        testId: ObjectSelectType.ObjectSelect,
        onClick: () => setIcon(ObjectSelectType.ObjectSelect),
        primaryIcon: [selectionToolIcons[ObjectSelectType.ObjectSelect].prefix, selectionToolIcons[ObjectSelectType.ObjectSelect].iconName],
        secondaryIcon: null,
    },
    {
        displayName: translate(Content.map9.select.select_area),
        testId: ObjectSelectType.AreaSelect,
        onClick: () => setIcon(ObjectSelectType.AreaSelect),
        primaryIcon: [selectionToolIcons[ObjectSelectType.AreaSelect].prefix, selectionToolIcons[ObjectSelectType.AreaSelect].iconName],
        secondaryIcon: null,
        disabled: noLayerSelected,
    },
];

export enum DrawingType {
    Polygon = "Polygon",
    AreaRectangle = "AreaRectangle",
    Line = "Line",
    LineRectangle = "LineRectangle",
    Route = "Route",
    Icon = "Icon",
    Point = "Point",
    Model = "Model",
}

const drawingTypeProperties: Record<
    DrawingType,
    {
        iconName: IconName;
        iconPrefix: IconPrefix;
        mapObjectProperties?: Partial<MapObjectProperties>;
    }
> = {
    [DrawingType.Polygon]: { iconName: "draw-polygon", iconPrefix: "far" },
    [DrawingType.AreaRectangle]: { iconName: "vector-square", iconPrefix: "far", mapObjectProperties: { fixedShape: FixedShape.Rectangle } },
    [DrawingType.Line]: { iconName: "line-nodes" as IconName, iconPrefix: "far" },
    [DrawingType.LineRectangle]: { iconName: "vector-square", iconPrefix: "far", mapObjectProperties: { fixedShape: FixedShape.Rectangle } },
    [DrawingType.Route]: { iconName: "route", iconPrefix: "far", mapObjectProperties: { modeOfTransport: ModeOfTransport.Car } },
    [DrawingType.Point]: { iconName: "crosshairs", iconPrefix: "far" },
    [DrawingType.Icon]: { iconName: "crosshairs", iconPrefix: "far" },
    [DrawingType.Model]: { iconName: "crosshairs", iconPrefix: "far" },
};

/** Holds a map of drawing type to style type */
const drawingTypeToStyleType: Record<DrawingType, StyleType> = {
    [DrawingType.Polygon]: StyleType.Area,
    [DrawingType.AreaRectangle]: StyleType.Area,
    [DrawingType.Line]: StyleType.Line,
    [DrawingType.LineRectangle]: StyleType.Line,
    [DrawingType.Route]: StyleType.Line,
    [DrawingType.Point]: StyleType.Point,
    [DrawingType.Icon]: StyleType.Icon,
    [DrawingType.Model]: StyleType.Model,
};

const drawAreaMenuItems = (setIcon: (drawingType: DrawingType) => void, translate: UseIventisTranslate) => [
    {
        displayName: translate(Content.map9.draw.polygon),
        testId: "draw-polygon",
        onClick: () => setIcon(DrawingType.Polygon),
        primaryIcon: [drawingTypeProperties[DrawingType.Polygon].iconPrefix, drawingTypeProperties[DrawingType.Polygon].iconName],
        secondaryIcon: null,
    },
    {
        displayName: translate(Content.map9.draw.rectangle),
        testId: "draw-rectangle",
        onClick: () => setIcon(DrawingType.AreaRectangle),
        primaryIcon: [drawingTypeProperties[DrawingType.AreaRectangle].iconPrefix, drawingTypeProperties[DrawingType.AreaRectangle].iconName],
        secondaryIcon: null,
    },
];

const drawLineMenuItems = (setIcon: (drawingType: DrawingType) => void, isModelLine = false, translate: UseIventisTranslate) => [
    {
        displayName: translate(Content.map9.draw.line),
        testId: "draw-line",
        onClick: () => setIcon(DrawingType.Line),
        primaryIcon: [drawingTypeProperties[DrawingType.Line].iconPrefix, drawingTypeProperties[DrawingType.Line].iconName],
        secondaryIcon: null,
    },
    {
        displayName: translate(Content.map9.draw.rectangle),
        testId: "draw-rectangle",
        onClick: () => setIcon(DrawingType.LineRectangle),
        primaryIcon: [drawingTypeProperties[DrawingType.LineRectangle].iconPrefix, drawingTypeProperties[DrawingType.LineRectangle].iconName],
        secondaryIcon: null,
    },
    {
        ...(!isModelLine && {
            displayName: translate(Content.map9.draw.routePlanner),
            testId: "draw-route",
            onClick: () => {
                setIcon(DrawingType.Route);
            },
            primaryIcon: [drawingTypeProperties[DrawingType.Route].iconPrefix, drawingTypeProperties[DrawingType.Route].iconName],
            secondaryIcon: null,
        }),
    },
];

const drawPointMenuItems = (setIcon: (drawingIcon: DrawingType) => void, translate: UseIventisTranslate) => [
    {
        displayName: translate(Content.map9.draw.point),
        testId: "draw-point",
        onClick: () => setIcon(DrawingType.Point),
        primaryIcon: [drawingTypeProperties[DrawingType.Point].iconPrefix, drawingTypeProperties[DrawingType.Point].iconName],
        secondaryIcon: null,
    },
    {
        displayName: translate(Content.map9.draw.model),
        testId: "draw-model",
        onClick: () => setIcon(DrawingType.Model),
        primaryIcon: [drawingTypeProperties[DrawingType.Model].iconPrefix, drawingTypeProperties[DrawingType.Model].iconName],
        secondaryIcon: null,
    },
    {
        displayName: translate(Content.map9.draw.icon),
        testId: "draw-icon",
        onClick: () => setIcon(DrawingType.Icon),
        primaryIcon: [drawingTypeProperties[DrawingType.Icon].iconPrefix, drawingTypeProperties[DrawingType.Icon].iconName],
        secondaryIcon: null,
    },
];
export const isAnalysisToolLine = (id: string) => id === AnalysisType.Distance || id === AnalysisType.WalkTime || id === AnalysisType.FlowRate;

const getTooltip = (selectedButtonId: string, options: { id: string; displayText: string }[]) => options.filter((option) => option.id === selectedButtonId)[0]?.displayText;

/** Maps a drawing modifier to its allowed drawing types */
const drawingModifierDrawingTypes: Record<DrawingModifier, DrawingType[]> = {
    none: [],
    snapping: [DrawingType.Point, DrawingType.Icon, DrawingType.Model, DrawingType.Polygon, DrawingType.Line, DrawingType.AreaRectangle, DrawingType.LineRectangle],
};

/** Maps a style type to its default drawing type */
const styleTypeToDefaultDrawingType: Record<StyleType, DrawingType> = {
    [StyleType.Area]: DrawingType.Polygon,
    [StyleType.Line]: DrawingType.Line,
    [StyleType.LineModel]: DrawingType.Line,
    [StyleType.AreaModel]: DrawingType.Polygon,
    [StyleType.Point]: DrawingType.Point,
    [StyleType.Icon]: DrawingType.Icon,
    [StyleType.Model]: DrawingType.Model,
};

export const DrawToolBarButtons = ({
    layerType,
    selectedButton,
    eventStream,
    commentsData,
    deleteData,
    onAnalysisToolClicked,
    drawingButtonData,
    copyButtonData,
    disableUndoRedo,
    objectSelectData,
    drawingToolTip,
    handleInfoTooltipClose,
    handleSetSelectedAnalysisTool,
    handleHideComments,
    drawingModifiers,
    selectedFeatures,
    composingNewObject,
    isExternalUser,
    lineSplitting,
}: {
    layerType: StyleType;
    selectedButton: AnalysisType | DrawingType | ObjectSelectType | CommentModeType | EditingAction;
    eventStream: EventStream<ModeEvents | MapEvents>;
    commentsData: { hideComments: boolean };
    deleteData: { deleteSelectedObjects: () => void; isDeleting: boolean; disabled: boolean };
    onAnalysisToolClicked: (analysisTool: AnalysisType) => void;
    drawingButtonData: {
        canUserDraw: boolean;
        startDrawing: (properties: Partial<Pick<MapObjectProperties, "fixedShape" | "modeOfTransport">>) => void;
        handleCancelDrawing: () => void;
        handleRoutePlannerClick: () => void;
        disabled: boolean;
        disableDrawingCompleteButton: boolean;
        disableAnalysisCompleteButton: boolean;
    };
    isExternalUser: boolean;
    copyButtonData: { disabled: boolean };
    disableUndoRedo: boolean;
    objectSelectData: { handleAreaSelect: () => void; layersSelected: boolean };
    drawingToolTip: string;
    handleInfoTooltipClose: () => void;
    handleSetSelectedAnalysisTool: (analysisTool: AnalysisType) => void;
    handleHideComments: () => void;
    drawingModifiers: {
        current: DrawingModifier;
        setModifier: (modifier: DrawingModifier) => void;
    };
    lineSplitting: {
        tooltip: string;
        disabled: boolean;
        onClick: () => void;
        cancel: () => void;
    };
    selectedFeatures: TypedFeature[];
    composingNewObject: boolean;
}) => {
    const translate = useIventisTranslate();
    const [lastAnalysisTool, setlastAnalysisTool] = useState<AnalysisType>(AnalysisType.Distance);
    const [selectedDrawTool, setSelectedDrawTool] = useState<DrawingType>(DrawingType.Polygon);
    const { hideComments } = commentsData;
    const { isDeleting, deleteSelectedObjects, disabled: deleteDisabled } = deleteData;
    const { handleAreaSelect, layersSelected } = objectSelectData;
    const {
        canUserDraw,
        startDrawing,
        disabled: drawingDisabled,
        handleCancelDrawing,
        handleRoutePlannerClick,
        disableAnalysisCompleteButton,
        disableDrawingCompleteButton,
    } = drawingButtonData;
    const { disabled: copyDisabled } = copyButtonData;

    // Undo/redo
    const { undo, canUndo, redo, canRedo } = useContext(UndoRedoContext);
    const undoDisabled = !canUndo || disableUndoRedo;
    const redoDisabled = !canRedo || disableUndoRedo;

    const theme = useTheme<Theme>();

    useEffect(() => {
        setSelectedDrawTool((drawingTool) => {
            let newDrawTool = drawingTool;
            // If we're composing a new object, we would have already set the correct drawing type
            // Unless a new layer has been added, so therefore check the drawing tool matches the style type
            if (composingNewObject && drawingTypeToStyleType[drawingTool] === layerType) {
                return newDrawTool;
            }
            newDrawTool = styleTypeToDefaultDrawingType[layerType] ?? DrawingType.Polygon;
            if (selectedFeatures?.length) {
                const allRoutes = selectedFeatures.every((geo) => geo.properties?.modeOfTransport != null);
                if (allRoutes) {
                    newDrawTool = DrawingType.Route;
                }
                const allRectangles = selectedFeatures.every((geo) => geo.properties?.fixedShape === FixedShape.Rectangle);
                if (allRectangles) {
                    newDrawTool = layerType === StyleType.Line ? DrawingType.LineRectangle : DrawingType.AreaRectangle;
                }
            }
            return newDrawTool;
        });
    }, [selectedFeatures, layerType, composingNewObject]);

    /** Is it possible to toggle snapping in this current state */
    const isSnappingAllowed = drawingModifierDrawingTypes.snapping.includes(selectedDrawTool) && canUserDraw && selectedButton !== EditingAction.LineSplitting;
    /** Is snapping currently enabled */
    const isSnappingEnabled = isSnappingAllowed && drawingModifiers.current === "snapping";

    const ctrlKey = useCtrlKey();
    return (
        <>
            <>
                <ActionDropdownButton
                    primaryButtonTestId="selection-tool-primary"
                    secondaryButtonTestId="selection-tool-secondary"
                    disable={false}
                    actionIcon={
                        <FontAwesomeIcon
                            icon={
                                Object.values(ObjectSelectType).includes(selectedButton as ObjectSelectType)
                                    ? selectionToolIcons[selectedButton]
                                    : (selectionToolIcons[ObjectSelectType.ObjectSelect] as IconProp)
                            }
                            size="xl"
                        />
                    }
                    clickHandler={() => eventStream.next({ type: machineEventTypes.enterObjectSelect })}
                    menuItems={selectionToolMenuItems(
                        (icon) => {
                            if (icon === ObjectSelectType.AreaSelect) handleAreaSelect();
                        },
                        !layersSelected,
                        translate
                    )}
                    tooltipPrimary={
                        <KeyboardShortcutToolTipComponent
                            header={getTooltip(Object.values(ObjectSelectType).includes(selectedButton as ObjectSelectType) ? selectedButton : ObjectSelectType.ObjectSelect, [
                                { id: ObjectSelectType.AreaSelect, displayText: translate(Content.map9.select.select_area) },
                                { id: ObjectSelectType.ObjectSelect, displayText: translate(Content.map9.select.select_object) },
                            ])}
                        />
                    }
                    variant="secondary"
                    isSelected={selectedButton === ObjectSelectType.ObjectSelect || selectedButton === ObjectSelectType.AreaSelect}
                />

                <StyledDivider />
                <span>
                    <DrawingTooltip
                        text={translate(Content.map9.drawMapObjectTooltip)}
                        open={drawingToolTip === "click-to-draw-again"}
                        onClose={handleInfoTooltipClose}
                        placement="bottom-start"
                        userPreference
                        className="drawing-tooltip"
                    >
                        <span>
                            <ActionDropdownButton
                                primaryButtonTestId="drawing-tool-primary"
                                secondaryButtonTestId="drawing-tool-secondary"
                                disable={drawingDisabled}
                                actionIcon={
                                    <FontAwesomeIcon icon={[drawingTypeProperties[selectedDrawTool].iconPrefix, drawingTypeProperties[selectedDrawTool].iconName]} size="xl" />
                                }
                                clickHandler={() => {
                                    startDrawing(drawingTypeProperties[selectedDrawTool].mapObjectProperties ?? {});
                                    if (drawingToolTip === "click-to-draw-again") {
                                        handleInfoTooltipClose();
                                    }
                                }}
                                menuItems={
                                    styleTypeToGeoJSONGeometryType(layerType == null ? StyleType.Area : layerType) === "LineString"
                                        ? drawLineMenuItems(
                                              (icon: DrawingType) => {
                                                  setSelectedDrawTool(icon);
                                                  if (icon === DrawingType.Route) {
                                                      handleRoutePlannerClick();
                                                  } else {
                                                      startDrawing(drawingTypeProperties[icon].mapObjectProperties ?? {});
                                                  }
                                              },
                                              layerType === StyleType.LineModel,
                                              translate
                                          )
                                        : styleTypeToGeoJSONGeometryType(layerType == null ? StyleType.Area : layerType) === "Point"
                                        ? drawPointMenuItems((icon: DrawingType) => {
                                              setSelectedDrawTool(icon);
                                              startDrawing({});
                                          }, translate)
                                        : // Area LayerType
                                          drawAreaMenuItems((icon: DrawingType) => {
                                              setSelectedDrawTool(icon);
                                              startDrawing(drawingTypeProperties[icon].mapObjectProperties ?? {});
                                          }, translate)
                                }
                                tooltipPrimary={
                                    <KeyboardShortcutToolTipComponent
                                        header={getTooltip(selectedDrawTool, [
                                            { id: DrawingType.Polygon, displayText: translate(Content.map3.toolbarButtonTooltips.draw_analysis_polygon) },
                                            { id: DrawingType.AreaRectangle, displayText: translate(Content.map3.toolbarButtonTooltips.draw_rectangle) },
                                            { id: DrawingType.Line, displayText: translate(Content.map3.toolbarButtonTooltips.draw_analysis_line) },
                                            { id: DrawingType.LineRectangle, displayText: translate(Content.map9.drawLineRectangle) },
                                            { id: DrawingType.Route, displayText: translate(Content.map9.drawRoute) },
                                            { id: DrawingType.Icon, displayText: translate(Content.map3.toolbarButtonTooltips.draw_icon) },
                                            { id: DrawingType.Point, displayText: translate(Content.map3.toolbarButtonTooltips.draw_point) },
                                            { id: DrawingType.Model, displayText: translate(Content.map3.toolbarButtonTooltips.draw_model) },
                                        ])}
                                    />
                                }
                                variant="secondary"
                                isSelected={Object.values(DrawingType).includes(selectedButton as DrawingType)}
                                disableSecondary={selectedDrawTool === DrawingType.Point || selectedDrawTool === DrawingType.Model || selectedDrawTool === DrawingType.Icon}
                            />
                        </span>
                    </DrawingTooltip>
                </span>
                <ActionDropdownButton
                    primaryButtonTestId="analysis-tool-primary"
                    secondaryButtonTestId="analysis-tool-secondary"
                    disable={false}
                    actionIcon={<FontAwesomeIcon size="xl" icon={analysisIcons[lastAnalysisTool] as IconProp} />}
                    clickHandler={() => {
                        handleSetSelectedAnalysisTool(lastAnalysisTool);
                        const layerId = isAnalysisToolLine(lastAnalysisTool) ? analysisLineLayerId : analysisPolygonLayerId;
                        eventStream.next({ type: machineEventTypes.composeAnalysis, payload: { objects: [{ layerId }] } });
                    }}
                    menuItems={analysisMenuItems(
                        (icon) => {
                            setlastAnalysisTool(icon);
                        },
                        onAnalysisToolClicked,
                        translate
                    )}
                    tooltipPrimary={
                        <KeyboardShortcutToolTipComponent
                            header={getTooltip(lastAnalysisTool, [
                                { id: AnalysisType.Distance, displayText: translate(Content.map2.analysis.distance_title) },
                                { id: AnalysisType.WalkTime, displayText: translate(Content.map2.analysis.walk_time_title) },
                                { id: AnalysisType.FlowRate, displayText: translate(Content.map2.analysis.flow_rate_title) },
                                { id: AnalysisType.Area, displayText: translate(Content.map2.analysis.area_title) },
                                { id: AnalysisType.Count, displayText: translate(Content.map2.analysis.count_title) },
                                { id: AnalysisType.Capacity, displayText: translate(Content.map2.analysis.capacity_title) },
                            ])}
                        />
                    }
                    variant="secondary"
                    isSelected={Object.values(AnalysisType).includes(selectedButton as AnalysisType)}
                />

                <CommentsButton
                    action={() => eventStream.next({ type: machineEventTypes.toggleComments })}
                    selected={selectedButton === CommentModeType.Commenting}
                    disabled={false}
                    colour={theme.typographyColors.core}
                    hoverColour={selectedButton === CommentModeType.Commenting ? theme.typographyColors.core : theme.tertiaryColors.primaryBackground}
                    selectedColour={theme.typographyColors.blank}
                    selectedBackgroundColour={selectedButton === CommentModeType.Commenting ? theme.typographyColors.core : theme.typographyColors.blank}
                    hoverSelectedColour={selectedButton === CommentModeType.Commenting ? theme.typographyColors.blank : theme.typographyColors.core}
                    disabledColour={theme.shades.three}
                    buttonWidth={toolbarCommentButtonWidth}
                    buttonHeight={toolbarCommentButtonHeight}
                    padding={toolbarCommentButtonPadding}
                    tooltipHeader={translate(selectedButton === CommentModeType.Commenting ? Content.comments.cancelComment : Content.comments.comment)}
                />

                {selectedButton === CommentModeType.Commenting && (
                    <CommentsVisibilityButton
                        action={handleHideComments}
                        selected={hideComments}
                        disabled={false}
                        colour={theme.typographyColors.core}
                        hoverColour={theme.tertiaryColors.primaryBackground}
                        selectedColour={theme.typographyColors.core}
                        hoverSelectedColour={theme.typographyColors.core}
                        disabledColour={theme.shades.three}
                        buttonWidth={toolbarButton}
                        buttonHeight={toolbarButton}
                        padding={toolbarButtonPadding}
                        tooltipHeader={hideComments ? translate(Content.comments.showComments) : translate(Content.comments.hideComments)}
                    />
                )}
            </>
            {/* If undo or redo is available, we should show the sub drawing toolbar */}
            {!isExternalUser && (canUserDraw || canUndo || canRedo) && selectedButton !== CommentModeType.Commenting && (
                <SubDrawingTools data-testid="drawing-toolbar-sub-menu">
                    <ConfirmAction
                        action={() => eventStream.next({ type: machineEventTypes.finish })}
                        selected
                        disabled={
                            selectedButton === EditingAction.LineSplitting ||
                            selectedButton === ObjectSelectType.ObjectSelect ||
                            (Object.values(AnalysisType).includes(selectedButton as AnalysisType) && disableAnalysisCompleteButton) ||
                            (Object.values(DrawingType).includes(selectedButton as DrawingType) && disableDrawingCompleteButton)
                        }
                        colour={theme.typographyColors.core}
                        hoverColour={theme.toastColours.success}
                        selectedColour={theme.typographyColors.core}
                        disabledColour={theme.shades.darkBorder}
                        buttonWidth={smallToolbarButton}
                        buttonHeight={smallToolbarButton}
                        selectedBackgroundColour={
                            selectedButton === EditingAction.LineSplitting ||
                            selectedButton === ObjectSelectType.ObjectSelect ||
                            (Object.values(AnalysisType).includes(selectedButton as AnalysisType) && disableAnalysisCompleteButton) ||
                            (Object.values(DrawingType).includes(selectedButton as DrawingType) && disableDrawingCompleteButton)
                                ? theme.shades.lightBorder
                                : theme.toastColours.success
                        }
                        tooltipHeader={translate(Content.map4.toolbarButtonTooltips.confirm_action)}
                        tooltipShortcut={translate(Content.map9.tooltipShortcuts.confirm_drawing)}
                    />
                    <ToolBarButton
                        dataCy="cancel-drawing"
                        icon={["far", "xmark"]}
                        action={() => (selectedButton === EditingAction.LineSplitting ? lineSplitting.cancel() : handleCancelDrawing())}
                        selected={false}
                        disabled={selectedButton === ObjectSelectType.ObjectSelect}
                        colour={theme.typographyColors.core}
                        hoverColour={theme.toastColours.error}
                        selectedColour={theme.typographyColors.core}
                        disabledColour={theme.shades.darkBorder}
                        selectedBackgroundColour={selectedButton === ObjectSelectType.ObjectSelect ? theme.shades.lightBorder : theme.toastColours.error}
                        buttonWidth={smallToolbarButton}
                        buttonHeight={smallToolbarButton}
                        spin={false}
                        text={translate(Content.map.buttons.cancel)}
                        tooltipHeader={translate(Content.map8.cancelDrawing)}
                    />

                    {/* Snapping toggle */}
                    <ToolBarButton
                        disabled={!isSnappingAllowed}
                        text={translate(Content.map9.snapping.snapToDraw)}
                        action={() => drawingModifiers.setModifier(isSnappingEnabled ? "none" : "snapping")}
                        icon={["far", "magnet"]}
                        selected={isSnappingEnabled}
                        colour={theme.typographyColors.core}
                        hoverColour={undefined}
                        tooltipHeader={translate(Content.map9.snapping.snapToDraw)}
                        selectedColour={theme.typographyColors.blank}
                        disabledColour={theme.shades.three}
                        selectedBackgroundColour={theme.primaryColors.subdued70}
                        buttonWidth={smallToolbarButton}
                        buttonHeight={smallToolbarButton}
                        dataCy="snap-to"
                    />
                    <ToolBarButton
                        disabled={lineSplitting.disabled}
                        text={translate(Content.map11.splitLines.title)}
                        action={() => lineSplitting.onClick()}
                        icon={["far", "scissors"]}
                        selected={selectedButton === EditingAction.LineSplitting}
                        colour={theme.typographyColors.core}
                        hoverColour={undefined}
                        tooltipHeader={lineSplitting.tooltip}
                        selectedColour={theme.typographyColors.blank}
                        disabledColour={theme.shades.three}
                        selectedBackgroundColour={theme.primaryColors.subdued70}
                        buttonWidth={smallToolbarButton}
                        buttonHeight={smallToolbarButton}
                        dataCy="split-line"
                    />

                    <StyledDivider $spacing={4} />

                    <UndoButton
                        action={() => undo()}
                        selected={false}
                        disabled={undoDisabled}
                        colour={theme.typographyColors.core}
                        hoverColour={theme.tertiaryColors.primaryBackground}
                        selectedColour={theme.primaryColors.focus}
                        disabledColour={theme.shades.three}
                        buttonWidth={smallToolbarButton}
                        buttonHeight={smallToolbarButton}
                        padding={toolbarButtonPadding}
                        tooltipHeader={translate(Content.map3.toolbarButtonTooltips.undo)}
                        tooltipShortcut={`${ctrlKey} + Z`}
                        dataCy="undo"
                    />
                    <RedoButton
                        action={() => redo()}
                        selected={false}
                        disabled={redoDisabled}
                        colour={theme.typographyColors.core}
                        hoverColour={theme.tertiaryColors.primaryBackground}
                        selectedColour={theme.primaryColors.focus}
                        disabledColour={theme.shades.three}
                        buttonWidth={smallToolbarButton}
                        buttonHeight={smallToolbarButton}
                        padding={toolbarButtonPadding}
                        dataCy="redo"
                        tooltipHeader={translate(Content.map3.toolbarButtonTooltips.redo)}
                        tooltipShortcut={`${ctrlKey} + ⇧ + Z`}
                    />
                    <CopyMapObjects
                        action={() => eventStream.next({ type: machineEventTypes.copyObjects })}
                        selected={false}
                        disabled={copyDisabled}
                        colour={theme.typographyColors.core}
                        hoverColour={theme.tertiaryColors.primaryBackground}
                        selectedColour={theme.primaryColors.focus}
                        disabledColour={theme.shades.three}
                        buttonWidth={smallToolbarButton}
                        buttonHeight={smallToolbarButton}
                        padding={toolbarButtonPadding}
                        dataCy="copy"
                        tooltipHeader={translate(Content.map3.toolbarButtonTooltips.copy_map_objects)}
                        tooltipShortcut={`${ctrlKey} + C / ${ctrlKey} + V`}
                    />

                    <ToolBarButton
                        action={deleteSelectedObjects}
                        dataCy="delete"
                        icon={isDeleting ? ["fas", "circle-notch"] : ["far", "trash"]}
                        colour={isDeleting ? theme.typographyColors.primary : theme.typographyColors.core}
                        hoverColour={theme.tertiaryColors.primaryBackground}
                        selected={false}
                        spin={isDeleting}
                        disabled={deleteDisabled}
                        selectedColour={theme.primaryColors.focus}
                        disabledColour={theme.shades.three}
                        buttonWidth={smallToolbarButton}
                        buttonHeight={smallToolbarButton}
                        padding={toolbarButtonPadding}
                        text={translate(Content.map8.deleteObject)}
                        tooltipHeader={translate(Content.map8.deleteObject)}
                        tooltipShortcut={translate(Content.map9.tooltipShortcuts.delete_object)}
                    />
                </SubDrawingTools>
            )}
        </>
    );
};

const SubDrawingTools = styled.div`
    display: flex;
    left: 50%;
    transform: translateX(-50%);
    position: absolute;
    top: 60px;
    background-color: ${({ theme }) => theme.typographyColors.blank};
    padding: 5px;
    border-radius: ${borderRadius.input};
`;

const StyledDivider = styled.div<{ $spacing?: number }>`
    width: 1px;
    height: auto;
    margin: 4px ${({ $spacing }) => $spacing ?? 0}px;
    background-color: ${({ theme }) => theme.tertiaryColors.headerBackground};
`;

const DrawingTooltip = styled(InfoTooltip)`
    // Places the tooltip in the center of the drawing tool button
    translate: -20px;
`;
